import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useVideoPlayerRefs } from "../contexts/VideoPlayerRefs";
import { SourceProps } from "../types/sourceProps";

import Hls, {
  ErrorData,
  Events,
  LevelLoadedData,
  LevelSwitchedData,
  ManifestParsedData,
} from "hls.js";
import { CurrentQualityLevel, QualityLevel } from "../contexts/VideoSource";
import { createHlsSourceElement, isHls } from "../utils/hls";

const hlsConfig = (startPosition?: number) => ({
  debug: false,
  enableWorker: true,
  lowLatencyMode: true,
  backBufferLength: 90,
  startPosition,
});

export const useHlsSource = ({ startTime = 0, src }: SourceProps) => {
  const { video } = useVideoPlayerRefs();
  const hls = useRef<Hls | null>(null);
  const mediaErrorRaised = useRef<boolean>(false);
  const lastSrc = useRef<string>();

  const [currentQualityLevel, setCurrentQualityLevel] =
    useState<CurrentQualityLevel>({ isAuto: true, id: 0 });
  const [qualityLevels, setQualityLevels] = useState<QualityLevel[]>([]);

  const setQualityLevel = useCallback(
    (id: number) => {
      if (hls.current !== null) {
        hls.current.currentLevel = id;
      }
    },
    [hls]
  );

  const manifestParsedHandler = useCallback(
    (_e: Events.MANIFEST_PARSED, data: ManifestParsedData) => {
      setQualityLevels(
        data.levels.map((level, index) => ({
          id: index,
          label: `${level.height.toString()}p`,
        }))
      );
    },
    []
  );

  const levelLoadedHandler = useCallback(
    (_e: Events.LEVEL_LOADED, data: LevelLoadedData) => {
      setCurrentQualityLevel({
        id: data.id,
        isAuto: hls.current?.autoLevelEnabled || false,
      });
    },
    []
  );

  const levelSwitchedHandler = useCallback(
    (_e: Events.LEVEL_SWITCHED, data: LevelSwitchedData) => {
      setCurrentQualityLevel({
        id: data.level,
        isAuto: hls.current?.autoLevelEnabled || false,
      });
    },
    []
  );

  const errorHandler = useCallback((_e: Events.ERROR, data: ErrorData) => {
    if (data.fatal) {
      switch (data.type) {
        case Hls.ErrorTypes.NETWORK_ERROR:
          // console.log("Hls - Fatal Network Error", data);
          hls.current?.startLoad();
          break;
        case Hls.ErrorTypes.MEDIA_ERROR:
          // console.log("Hls - Fatal Media Error", data);
          if (mediaErrorRaised.current) {
            hls.current?.swapAudioCodec();
            mediaErrorRaised.current = false;
          }
          hls.current?.recoverMediaError();
          mediaErrorRaised.current = true;
          break;
        default:
          // console.log("Hls - Other Fatal Error", data);
          hls.current?.destroy();
          break;
      }
    } else {
      // console.log("Hls - Nonfatal Error", data);
    }
  }, []);

  const loadVideo = useCallback(
    (src: string) => {
      if (video.current !== null) {
        if (isHls(src) && Hls.isSupported()) {
          hls.current = new Hls(hlsConfig(startTime));
          hls.current.on(Hls.Events.MANIFEST_PARSED, manifestParsedHandler);
          hls.current.on(Hls.Events.LEVEL_LOADED, levelLoadedHandler);
          hls.current.on(Hls.Events.LEVEL_SWITCHED, levelSwitchedHandler);
          hls.current.on(Hls.Events.ERROR, errorHandler);

          hls.current.loadSource(src);
          hls.current.attachMedia(video.current);
        } else {
          video.current.load();
          const element = createHlsSourceElement(src);

          video.current.appendChild(element);
        }
      }
    },
    [
      video,
      startTime,
      manifestParsedHandler,
      levelLoadedHandler,
      levelSwitchedHandler,
      errorHandler,
    ]
  );

  // they are flaming idiots

  useEffect(() => {
    if (video.current !== null && src !== undefined) {
      const element = video.current;
      loadVideo(src);
      lastSrc.current = src;

      return () => {
        if (hls.current !== null) {
          hls.current.destroy();
        }

        while (element.firstChild !== null) {
          element.removeChild(element.firstChild);
        }
      };
    }
  }, [loadVideo, src, video]);

  return useMemo(
    () =>
      src !== undefined && isHls(src)
        ? {
            setQualityLevel,
            qualityLevels,
            currentQualityLevel,
          }
        : undefined,

    [currentQualityLevel, qualityLevels, setQualityLevel, src]
  );
};
